import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import {
  arrayRemove,
  arraySplice,
  change as changeField,
  Field,
  FieldArray,
  Fields,
  getFormSyncErrors,
  getFormValues,
  initialize,
  reduxForm,
} from 'redux-form';

import cls from 'classnames/bind';

import _findLastIndex from 'lodash/findLastIndex';
import _isEqual from 'lodash/isEqual';
import qs from 'qs';

import { getAvailableTests } from '@ott/ab-test';
import { BusinessTravelCheckbox } from '@ott/business-travel-checkbox';
import Button from '@ott/button';
import FirstErrorShower from '@ott/first-error-shower';
import BaseForm from '@ott/form';
import ComplexRouteIcon from '@ott/icon-complex-route';
import PlusIcon from '@ott/icon-plus';
import SimpleRoute from '@ott/icon-simple-route';
import { l10n, lang } from '@ott/l10n';
import SearchHistory, {
  getRoutesFromLocalStorage,
  saveSearchRouteInLocalStorage,
} from '@ott/search-history';
import crossSearch from '@ott/utility-cross-search';
import { withEnterSubmit } from '@ott/utility-enter-submit';

import { analytics } from './analytics';
import Flight, { focusOnFirstEmptyField } from './blocks/Flight/Flight';
import ServiceClasses from './blocks/ServiceClasses/ServiceClasses';
import ServiceClassesDropDown from './blocks/ServiceClassesDropDown/ServiceClassesDropDown';
import TravellersWithClasses from './blocks/TravellersWithClasses/TravellersWithClasses';
import { getFlightsFieldValue } from './helpers/getFlightsFieldValue';
import getFlightsSearchQueryParams from './helpers/getFlightsSearchQueryParams';
import parseRoute from './helpers/parseRoute';
import {
  checkNewSearchRoute,
  checkSearchHistoryFromStorage,
  getFormattedRouteFromLocalStorage,
} from './helpers/searchHistoryLocalStorage';
import validateHelpers from './helpers/validate';

import s from './FlightsSearchForm.scss';

const cx = cls.bind(s);
const isSSR = typeof window === 'undefined';
const serviceClassMap = {
  E: 'economy',
  W: 'premium',
  B: 'business',
  F: 'first',
};

const { hasValidationErrors, validateForm, validateSync } = validateHelpers;

const defaultFormName = 'FlightsSearchForm';

const MAX_FLIGHTS_COUNT = 4;
const ITEMS_COUNT = 3;
const MAX_ADULTS = 9;
const MAX_CHILDREN = 8;
const MAX_INFANT_AGE = 2;

const serviceClassesKeys = Object.values(serviceClassMap);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { route, serviceClass, passengers, formName = defaultFormName } = ownProps;

  let props = {
    ...ownProps,
    ...dispatchProps,
    formValues: getFormValues(formName)(stateProps),
    errors: getFormSyncErrors(formName)(stateProps),
  };

  const { adultsCount = 1, childrenAges = [] } = passengers;

  const initialValues = {
    serviceClass: serviceClassMap[serviceClass],
    adultsCount,
    childrenAges,
    flights: getFlightsFieldValue(route),
  };

  props = {
    ...props,
    initialValues,
    form: formName,
  };

  if (!stateProps.form[formName]) {
    props.formValues = initialValues;
  }

  return props;
};

@connect((state) => state, (dispatch) => ({ dispatch }), mergeProps)
@reduxForm({
  validate: validateSync,
})
@withEnterSubmit({ shouldFieldHasFocus: true })
class FlightsSearchForm extends BaseForm {
  static displayName = 'FlightsSearchForm';

  static propTypes = {
    withHeatmap: PropTypes.bool,
    route: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)).isRequired,
    passengers: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)).isRequired,
    onFormSubmit: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
    toggleTopArea: PropTypes.func,
    onMountCallback: PropTypes.func,
    onUnmountCallback: PropTypes.func,
    formName: PropTypes.string,
    referrer: PropTypes.string,
    isSearchForExchange: PropTypes.bool,
    isExchangeReroutingEnable: PropTypes.bool,
    enableInitialFocus: PropTypes.bool,
    searchUrlPathname: PropTypes.string,
    // Дата которая будет выставлена в значение toDate, если это поле undefined.
    defaultStartDate: PropTypes.instanceOf(Date),
    // Дата которая будет выставлена в значение fromDate, если это поле undefined и выставлен режим "Туда-обратно".
    defaultEndDate: PropTypes.instanceOf(Date),
    showBusinessTripCheckbox: PropTypes.bool,
  };

  static defaultProps = {
    withHeatmap: true,
    forceNextFocus: true,
    searchUrlPathname: `/${lang}/f/search`,
    isMobile: false,
    formName: defaultFormName,
    referrer: '',
    isSearchForExchange: false,
    isExchangeReroutingEnable: false,
    enableInitialFocus: true,
    showBusinessTripCheckbox: false,
  };

  constructor(props, ctx) {
    super(props, ctx);

    const { route, serviceClass, passengers, defaultStartDate, defaultEndDate } = this.props;

    const availableTests = getAvailableTests();
    const isOWInCookie = availableTests.includes('ow_def');

    this.state = {
      isRT: !isOWInCookie && Boolean(route.isRT),
      isClient: false,
    };

    const { adultsCount = 1, childrenAges = [] } = passengers;

    const formData = {
      serviceClass: serviceClassMap[serviceClass],
      adultsCount,
      childrenAges,
      flights: getFlightsFieldValue(route),
    };
    if (route.directions.length === 0) {
      if (defaultStartDate) {
        formData.flights[0].dates.toDate = defaultStartDate;
      }
      if (defaultEndDate) {
        formData.flights[0].dates.fromDate = defaultEndDate;
      }
    }

    this.props.initialize(formData);
  }

  componentDidMount() {
    const { onMountCallback } = this.props;

    this.setState({ isClient: true }, () => this.initializeFromHistory());

    window.addEventListener('pageshow', this.reloadForPersistedPage);

    analytics.serpSearchOpen();
    if (onMountCallback) {
      onMountCallback();
    }
  }

  componentDidUpdate(prevProps) {
    const { defaultStartDate, defaultEndDate, passengers, route, serviceClass } = this.props;
    if (!prevProps.defaultStartDate && defaultStartDate && !getFormattedRouteFromLocalStorage()) {
      const { adultsCount = 1, childrenAges = [] } = passengers;
      const formData = {
        serviceClass: serviceClassMap[serviceClass],
        adultsCount,
        childrenAges,
        flights: getFlightsFieldValue(route),
      };
      if (route.directions.length === 0) {
        if (defaultStartDate) {
          formData.flights[0].dates.toDate = defaultStartDate;
        }
        if (defaultEndDate) {
          formData.flights[0].dates.fromDate = defaultEndDate;
        }
        this.props.initialize(formData);
      }
    }
  }

  reloadForPersistedPage = (event) => {
    if (event.persisted) {
      this.initializeFromHistory();
    }
  };

  initializeFromHistory() {
    const { actions, dispatch, route, isMobile, formName } = this.props;
    const fromField = 'flights[0].from';
    const toField = 'flights[0].to';

    if (route.directions.length) {
      return;
    }
    const formattedRoute = getFormattedRouteFromLocalStorage();
    const crossSearchData = crossSearch.getAvia();
    if (crossSearchData) {
      let formData = crossSearchData;
      if (formattedRoute && formattedRoute.serviceClass) {
        crossSearchData.serviceClass = serviceClassMap[formattedRoute.serviceClass];
      }
      const { flights } = crossSearchData;
      const {
        to: { text },
      } = flights[0];

      dispatch(actions.suggestDest(text, `${formName}.${toField}`));
      if (formattedRoute) {
        const { from } = getFlightsFieldValue(formattedRoute.route)[0];
        formData = {
          ...formData,
          flights: [
            {
              ...formData.flights[0],
              from: {
                ...formData.flights[0].from,
                text: from.text,
                code: from.text,
              },
            },
          ],
        };
        dispatch(actions.suggestDest(from.text, `${formName}.${fromField}`));
        const { route: routeByQueryParam, query } = getFlightsSearchQueryParams(formData);

        saveSearchRouteInLocalStorage(
          {
            route: parseRoute(routeByQueryParam),
            query,
          },
          'historyRoutes',
          3,
          checkNewSearchRoute
        );
      }
      this.setState({ isRT: true });
      this.props.initialize(formData);

      return;
    }

    if (formattedRoute && !isMobile) {
      // eslint-disable-next-line no-shadow
      const { route, serviceClass, adultsCount, childrenAges } = formattedRoute;
      const { from, to } = getFlightsFieldValue(route)[0];

      const initialValues = {
        serviceClass: serviceClassMap[serviceClass],
        adultsCount,
        childrenAges,
        flights: getFlightsFieldValue(route),
      };

      dispatch(actions.suggestDest(from.text, `${formName}.${fromField}`));
      dispatch(actions.suggestDest(to.text, `${formName}.${toField}`));

      this.setState({ isRT: route.isRT });
      this.props.initialize(initialValues);
    }
  }

  componentWillUnmount() {
    const { onUnmountCallback } = this.props;

    if (onUnmountCallback) {
      onUnmountCallback();
    }

    window.removeEventListener('pageshow', this.reloadForPersistedPage);
  }

  submit(e) {
    const { handleSubmit, onFormSubmit } = this.props;
    const { isRT } = this.state;

    handleSubmit(
      (data) =>
        new Promise((resolve, reject) => {
          const errors = {
            ...validateForm(data),
          };

          if (hasValidationErrors(errors)) {
            reject(errors);

            return;
          }

          const { route } = getFlightsSearchQueryParams(data);
          const typeDirection = isRT ? 'RT' : 'OW';
          analytics.serpSearchStart({ typeDirection, route });

          const { adultsCount, childrenAges, flights } = data;
          const {
            dates: { toDate, fromDate },
            to: { code },
          } = flights[0];

          crossSearch.setAvia({
            code,
            dateStart: toDate,
            dateEnd: fromDate,
            adults: adultsCount,
            childrenAges,
          });

          onFormSubmit(data);
          resolve();
        })
    )(e);
  }

  searchRoute = ({ data: routeWithLink }) => {
    const { onFormSubmit } = this.props;
    const formattedRoute = getFormattedRouteFromLocalStorage(routeWithLink.route);

    if (!formattedRoute) {
      return;
    }

    const { route, serviceClass, adultsCount, childrenAges } = formattedRoute;

    const formData = {
      serviceClass: serviceClassMap[serviceClass],
      adultsCount,
      childrenAges,
      flights: getFlightsFieldValue(route),
    };

    this.setState({ isRT: route.isRT });
    this.props.initialize(formData);
    onFormSubmit({ formData, ...routeWithLink });
  };

  prepareSearchHistoryItems() {
    const { searchUrlPathname, isMobile } = this.props;

    const routes = getRoutesFromLocalStorage(
      'historyRoutes',
      ITEMS_COUNT,
      checkSearchHistoryFromStorage
    );

    return (routes || []).map((routeSearch) => {
      const { route } = routeSearch;
      const searchParamsString = qs.stringify(routeSearch.query);
      const href = `${searchUrlPathname}/${route.routeString}?${searchParamsString}`;

      const routeDirections = route.directions || [];
      const directions = isMobile ? routeDirections.slice(0, 2) : routeDirections;

      return {
        from: route.fromCode,
        to: route.toCode,
        fromDate: new Date(route.fromDate),
        toDate: new Date(route.toDate),
        isRT: route.isRT,
        isMW: route.isMW,
        url: href,
        directions: directions.map(({ fromCode: from, toCode: to, date }) => ({
          from,
          to,
          date: new Date(date),
        })),
        data: {
          url: href,
          route: routeSearch,
        },
      };
    });
  }

  getCountOfInfants = (childrenAges) => {
    return childrenAges.reduce((acc, item) => {
      if (item < MAX_INFANT_AGE) {
        // eslint-disable-next-line no-param-reassign
        acc++;
      }

      return acc;
    }, 0);
  };

  handleAdultsChange = (adultsCount) => {
    const {
      formValues: { childrenAges },
      formName,
    } = this.props;

    const infantCount = this.getCountOfInfants(childrenAges);
    this.props.dispatch(changeField(formName, 'adultsCount', adultsCount));

    if (infantCount > adultsCount) {
      const infantIndex = _findLastIndex(childrenAges, (item) => item < MAX_INFANT_AGE);

      if (infantIndex >= 0) {
        childrenAges.splice(infantIndex, 1);
      }
      this.props.dispatch(changeField(formName, 'childrenAges', childrenAges));
    }
  };

  handleChildrenAgesChange = (childrenAges) => {
    const {
      formValues: { adultsCount },
      formName,
    } = this.props;

    const infantCount = this.getCountOfInfants(childrenAges);
    this.props.dispatch(changeField(formName, 'childrenAges', childrenAges));

    if (infantCount > adultsCount) {
      this.props.dispatch(changeField(formName, 'adultsCount', adultsCount + 1));
    }
  };

  handleChangeService = (selectedClass) => {
    const { formName, dispatch } = this.props;
    dispatch(changeField(formName, 'serviceClass', selectedClass));
  };

  handleDateBlur = (flightField) => {
    const { dates: field } = flightField;

    if (field.toDate && !field.fromDate) {
      this.setState({ isRT: false });
    }
  };

  clearAllDatesAfterCurrentFlight = (currentFlightIndex) => {
    const { formValues } = this.props;
    const { flights = [], adultsCount = 1, childrenAges = [], serviceClass } = formValues;

    const flightsWithClearedDates = flights.map((flight, index) => {
      if (index > currentFlightIndex) {
        return {
          ...flight,
          dates: {},
        };
      }

      return flight;
    });
    const formData = {
      serviceClass,
      adultsCount,
      childrenAges,
      flights: flightsWithClearedDates,
    };
    this.props.initialize(formData);
  };

  // Moscow - Barcelona: to Barcelona, from Barcelona
  handleDateSelect = (flightField, fieldName, fieldIndex, toDate, fromDate) => {
    const { dates: field } = flightField;
    const {
      formName,
      formValues: { flights = [] },
      dispatch,
      deviceInfo,
    } = this.props;

    const { isTouchDevice } = deviceInfo;

    if (!this.state.isRT) {
      // eslint-disable-next-line no-param-reassign
      fromDate = undefined;
    }

    const previousDate = flights[fieldIndex]?.dates.toDate?.getTime();
    const hasMoreThanOneFlight = flights.length > 1;
    const currentDate = toDate?.getTime();

    if (hasMoreThanOneFlight && previousDate !== currentDate) {
      this.clearAllDatesAfterCurrentFlight(fieldIndex);
    }

    dispatch(
      changeField(formName, fieldName, {
        ...field.value,
        toDate,
        fromDate,
      })
    );

    if (!this.state.isRT && !isTouchDevice) {
      // eslint-disable-next-line no-use-before-define
      focusField(fieldName);
    }
  };

  updateHeatmap = (isRT, toDate, fromDate, isDirectFlights) => {
    const {
      dispatch,
      actions,
      formValues: { flights = [] },
      referrer,
    } = this.props;

    const { from = {}, to = {} } = flights[0];

    if (flights.length > 1) {
      dispatch(actions.updateHeatmap());
    } else if (from.code && to.code) {
      dispatch(
        actions.updateHeatmap(isRT, from.code, to.code, toDate, fromDate, referrer, isDirectFlights)
      );
    }
  };

  handleAutocompleteText = (text, name, index) => {
    const {
      dispatch,
      actions,
      formValues: { flights },
      formName,
    } = this.props;

    const fromCode = flights[index].from;

    if (!text) {
      // нажали на крестик в в инпуте в адаптиве
      dispatch(changeField(formName, name, {}));
    }

    dispatch(
      actions.suggestDest(text, `${formName}.${name}`, {
        from: fromCode,
        index,
      })
    );
  };

  handleDirectionSelect = (index, item, name, options = {}) => {
    const {
      dispatch,
      actions,
      formName,
      formValues: { flights },
      deviceInfo,
    } = this.props;

    const { isTouchDevice } = deviceInfo;

    const { from, to } = flights[index];

    const otherField = name === `flights[${index}].to` ? from : to;
    const itemCityInfo = item ? item.cityInfo : null;
    const nameCityInfo = otherField ? otherField.cityInfo : null;

    if (_isEqual(itemCityInfo, nameCityInfo)) {
      this.handleReverseSamePoint(name, item, index);

      return;
    }

    dispatch(changeField(formName, name, item || {}));
    dispatch(actions.resetSuggest(`${formName}.${name}`));

    if (options.selectNext && !isTouchDevice) {
      // eslint-disable-next-line no-use-before-define
      focusField(name, index);
    }
  };

  handleResetSuggest = (name) => {
    const { dispatch, actions } = this.props;
    dispatch(actions.resetSuggest(name));
  };

  handleGeoAutocompleteFocus = (name, index) => {
    const {
      dispatch,
      actions,
      formName,
      formValues: { flights },
    } = this.props;
    // получаем популярные направления, чтобы саджест показывался даже если поле заполнено
    const { from } = flights[index];
    dispatch(actions.suggestDest('', `${formName}.${name}`, { from, index }));
  };

  handleReverseSamePoint = (name, item, index) => {
    const { dispatch, formName, actions } = this.props;
    const fieldName = name === `flights[${index}].to` ? `from` : `to`;

    dispatch(changeField(formName, `flights[${index}].${fieldName}`, {}));
    dispatch(changeField(formName, name, item));
    dispatch(actions.resetSuggest(`${formName}.${name}`));
  };

  handleDirectionChanged = (flightField, fieldName, index, value) => {
    const isRT = value === 'roundTrip';
    if (isRT !== this.state.isRT) {
      if (isRT) {
        analytics.serpRT();
      } else {
        analytics.serpOW();
        const { dates } = flightField;
        const toDateValue = dates.toDate;
        this.handleDateSelect(flightField, fieldName, index, toDateValue);
      }
    }
    this.setState({ isRT });
  };

  handleReverseButtonClick = () => {
    const {
      dispatch,
      formValues: { flights },
      actions,
      formName,
    } = this.props;

    const { from, to } = flights[0];

    const fromField = 'flights[0].from';
    const toField = 'flights[0].to';

    dispatch(actions.resetSuggest(`${formName}.${fromField}`));
    dispatch(actions.resetSuggest(`${formName}.${toField}`));

    dispatch(changeField(formName, toField, from));
    dispatch(changeField(formName, fromField, to));
  };

  handleAddFlightButtonClick = () => {
    const {
      dispatch,
      actions,
      formValues: { flights, ...restFormValues },
      formName,
    } = this.props;

    const lastIndex = flights.length - 1;

    const { dates, to: { code } = {} } = flights[lastIndex];
    const newFlightField = {
      from: { code, text: code },
      to: {},
      dates: {
        toDate: dates.fromDate,
      },
    };

    const fieldName = `flights.${flights.length}`;

    dispatch(actions.resetSuggest(`${formName}.${fieldName}.from`));
    dispatch(actions.resetSuggest(`${formName}.${fieldName}.to`));
    // for multiway clear fromDate
    dispatch(
      changeField(formName, `flights[${lastIndex}].dates`, {
        toDate: dates.toDate,
      })
    );

    dispatch(
      initialize(formName, {
        flights: flights.concat(newFlightField),
        ...restFormValues,
      })
    );

    const formFlightsCount = flights.length;
    if (formFlightsCount === 1) {
      analytics.serpMD();
    }
  };

  handleRemoveFlightButtonClick = (flightIndex, e) => {
    e.preventDefault();

    const {
      route,
      formValues: { flights },
      formName,
    } = this.props;

    // remove flight
    this.props.dispatch(arrayRemove(formName, 'flights', flightIndex));

    const flightsCount = flights.length;
    const isRT = route.isRT && flightsCount === 2;

    if (isRT) {
      // set fromDate from second flight
      const fromDate = flights[1].dates.toDate;
      this.props.dispatch(changeField(formName, `flights[0].dates.fromDate`, fromDate));
    }

    if (flightIndex + 1 === flightsCount) {
      focusOnFirstEmptyField(flights[flightIndex - 1], `flights[${flightIndex - 1}]`, isRT);
    } else {
      focusOnFirstEmptyField(flights[flightIndex + 1], `flights[${flightIndex}]`, isRT);
    }
  };

  handleResetFlightsButtonClick = (e) => {
    e.preventDefault();
    const {
      dispatch,
      formValues: { flights },
      formName,
    } = this.props;

    dispatch(arraySplice(formName, 'flights', 1, flights.length - 1));
  };

  handleSuggestDest = (text, name) => {
    const { actions, dispatch, formName } = this.props;

    if (!text) {
      return;
    }

    dispatch(actions.suggestDest(text, `${formName}.${name}`));
  };

  // Moscow - Barcelona: to Barcelona, from Barcelona
  renderFlights = ({ fields, travellersNode }) => {
    const {
      formValues: { flights },
      suggests,
      heatmap,
      heatmapStatus,
      toggleTopArea,
      deviceInfo,
      formName,
      isSearchForExchange,
      isExchangeReroutingEnable,
      enableInitialFocus,
      submitRef,
      withHeatmap,
      showBusinessTripCheckbox,
    } = this.props;

    const { isTouchDevice } = deviceInfo;

    const result = fields.map((member, i) => {
      return (
        <Flight
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          formName={formName}
          submitRef={submitRef}
          onGeoAutocompleteFocus={this.handleGeoAutocompleteFocus}
          member={member}
          index={i}
          flights={flights}
          onSuggestDest={this.handleSuggestDest}
          suggests={suggests}
          heatmap={heatmap}
          heatmapStatus={heatmapStatus}
          updateHeatmap={this.updateHeatmap}
          withHeatmap={withHeatmap}
          isRT={this.state.isRT}
          travellersNode={travellersNode}
          onAutocompleteText={this.handleAutocompleteText}
          onDirectionSelect={this.handleDirectionSelect}
          onDateSelect={this.handleDateSelect}
          onBlurDate={this.handleDateBlur}
          onDirectionChanged={this.handleDirectionChanged}
          onReverseButtonClick={this.handleReverseButtonClick}
          onRemoveFlightButtonClick={this.handleRemoveFlightButtonClick}
          onResetSuggest={this.handleResetSuggest}
          isTouchDevice={isTouchDevice}
          toggleTopArea={isTouchDevice ? toggleTopArea : null}
          isSearchForExchange={isSearchForExchange}
          isExchangeReroutingEnable={isExchangeReroutingEnable}
          enableInitialFocus={enableInitialFocus}
          showBusinessTripCheckbox={showBusinessTripCheckbox}
        />
      );
    });

    return <div>{result}</div>;
  };

  renderTravellersField({ isComplex }) {
    const { formValues, deviceInfo, isSearchForExchange } = this.props;
    const { adultsCount, childrenAges, serviceClass } = formValues;

    const childrenCount = childrenAges.length;

    let maxAdults = MAX_ADULTS;
    let maxChildren = MAX_CHILDREN;
    let rangeStart = 0;

    const infantCount = this.getCountOfInfants(childrenAges);

    if (adultsCount + childrenCount >= MAX_ADULTS) {
      maxAdults = adultsCount;
      maxChildren = childrenCount;
    }

    if (adultsCount + childrenCount === MAX_CHILDREN && adultsCount / infantCount === 1) {
      rangeStart = MAX_INFANT_AGE;
    }

    if (isSearchForExchange) {
      return (
        <Field
          component={ServiceClassesDropDown}
          name="serviceClass"
          onChange={this.handleChangeService}
          isSearchForExchange={isSearchForExchange}
        />
      );
    }

    return (
      <Fields
        names={['adultsCount', 'childrenAges']}
        minAdults={1}
        maxAdults={maxAdults}
        maxChildren={maxChildren}
        childrenAgeRange={{ start: rangeStart, end: 12 }}
        selectedClass={serviceClass}
        component={TravellersWithClasses}
        deviceInfo={deviceInfo}
        isComplex={isComplex}
        onAdultsChange={this.handleAdultsChange}
        onChildrenChange={this.handleChildrenAgesChange}
        footer={
          <ServiceClasses
            onChangeServiceClass={this.handleChangeService}
            serviceClassesKeys={serviceClassesKeys}
            selectedClass={serviceClass}
          />
        }
      />
    );
  }

  render() {
    const {
      formValues,
      errors,
      className,
      submitFailed,
      deviceInfo,
      isSearchForExchange,
      formRef,
      showBusinessTripCheckbox,
    } = this.props;
    const { isTouchDevice } = deviceInfo;

    if (!isSSR && (!formValues || !formValues.flights || formValues.flights.length === 0)) {
      return null;
    }

    const { flights } = formValues;
    const { submitTime } = this.state;

    const isComplexRoute = flights.length > 1;
    const showAddFlightButton = flights.length <= MAX_FLIGHTS_COUNT - 1;

    const searchHistoryItems = this.prepareSearchHistoryItems();
    const handleHistoryLabelsOpen = () => {
      analytics.aviaHistoryLabelsShow({ labelsCount: searchHistoryItems.length });
    };

    return (
      <form
        ref={formRef}
        className={cx('FlightsSearchForm', className)}
        onSubmit={this.handleSubmit}
        autoComplete="off"
        name="lastpass-disable-search"
        data-locator="FlightsSearchForm"
      >
        <FirstErrorShower
          errors={errors}
          show={submitFailed}
          time={submitTime}
          container={this}
          effect="transition"
          product="avia"
          withFocus={!isTouchDevice}
        />
        <FieldArray
          name="flights"
          component={this.renderFlights}
          isComplexRoute={isComplexRoute}
          travellersNode={this.renderTravellersField({
            isComplex: false,
          })}
        />
        {isComplexRoute && (
          <div className={cx('complexRouteLastRow')} key="complexRouteLastRow">
            {showAddFlightButton && (
              <div className={cx('addFlightButtonContainer')}>
                <Button
                  className={cx('addFlightButton')}
                  purpose="neutral"
                  withFullWidth={true}
                  onClick={this.handleAddFlightButtonClick}
                  specificLocators="addFlightButton"
                >
                  <PlusIcon className={cx('addFlightButtonIcon')} />
                  {l10n('flightsSearchForm.addFlight')}
                </Button>
              </div>
            )}
            {this.renderTravellersField({ isComplex: true })}
            {!__WHITE_LABEL__ && showBusinessTripCheckbox && (
              <div className={cx('businessTravelWrapMobile')}>
                <BusinessTravelCheckbox vertical="avia" />
              </div>
            )}
            <div className={cx('complexRouteSubmit')}>
              <Button type="submit" withFullWidth={true} data-locator="complexRouteSubmitButton">
                {l10n('flightsSearchForm.filters.search')}
              </Button>
            </div>
          </div>
        )}
        {!isSearchForExchange && (
          <div className={cx('footer', { 'footer--complexRoute': isComplexRoute })}>
            {searchHistoryItems.length !== 0 && (
              <div className={cx('searchHistoryWrap')}>
                <SearchHistory
                  className={cx('searchHistory')}
                  handleSearch={this.searchRoute}
                  items={searchHistoryItems}
                  project="avia"
                  childrenClassNames={{ searchItem: cx('searchItem') }}
                  onOpen={handleHistoryLabelsOpen}
                />
              </div>
            )}
            {!__WHITE_LABEL__ && showBusinessTripCheckbox && (
              <div
                className={cx('businessTravelWrapDesktop', {
                  businessTravelWrapJustifyEnd: searchHistoryItems.length !== 0,
                })}
              >
                <BusinessTravelCheckbox vertical="avia" />
              </div>
            )}
            {isComplexRoute ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <span
                className={cx('complexRouteLink')}
                onClick={this.handleResetFlightsButtonClick}
                data-locator="simpleRouteLink"
              >
                <SimpleRoute w={16} h={16} className={cx('complexRouteIcon')} />
                {l10n('flightsSearchForm.simpleRoute')}
              </span>
            ) : (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <span
                className={cx('complexRouteLink')}
                onClick={this.handleAddFlightButtonClick}
                data-locator="complexRouteLink"
              >
                <ComplexRouteIcon w={16} h={16} className={cx('complexRouteIcon')} />
                {l10n('flightsSearchForm.complexRoute')}
              </span>
            )}
          </div>
        )}
      </form>
    );
  }
}

const focusDictionary = {
  from: 'to',
  to: 'dates',
  dates: 'travellers-trigger',
};

function focusField(name) {
  const [index, field] = name.split('.');
  const nextName =
    focusDictionary[field] === 'travellers-trigger'
      ? focusDictionary[field]
      : `${index}.${focusDictionary[field]}`;

  const element = document.querySelector(`[name="${nextName}"]`);
  if (element) {
    // После обновления реакта до 17 версии handleClickOutside в компонентах Travellers и Datepicker перестал корректно работать:
    // клик при выборе значения предыдущего поля инициализирует фокус на следующем поле и последующую установку хендлера handleClickOutside,
    // при этом сам клик успевает отловиться этим же хендлером, что вызывает закрытие оверлея у Travellers или Datepicker.
    // Поэтому приходится обернуть фокусировку в setTimeOut
    setTimeout(() => element.focus());
  }
}

export default FlightsSearchForm;
